import classNames from 'classnames';
import React, {
  ChangeEventHandler,
  FocusEventHandler,
  ReactNode,
  Ref,
  useMemo,
} from 'react';

import { useUniqueId } from 'utils/hooks';

import styles from './Input.module.scss';

type Status = 'neutral' | 'success' | 'error';
type StyleVariant = 'default' | 'contrast' | 'light-border';
type PrefixVariant = 'default' | 'transparent';

type Props = {
  inputProps: {
    id?: string;
    name?: string;
    ref?: Ref<HTMLInputElement>;
    placeholder?: string;
    value?: string;
    disabled?: boolean;
    readOnly?: boolean;
    'data-qa-id'?: string;
    'aria-label'?: string;
    onChange?: ChangeEventHandler<HTMLInputElement>;
    onBlur?: FocusEventHandler<HTMLInputElement>;
    onFocus?: FocusEventHandler<HTMLInputElement>;
  };
  prefix?: ReactNode;
  suffix?: ReactNode;
  status?: Status;
  prefixVariant?: PrefixVariant;
  styleVariant?: StyleVariant;
  fullHeight?: boolean;
};

export function Input({
  inputProps: {
    id,
    name,
    ref,
    placeholder,
    value,
    disabled,
    readOnly,
    onChange,
    onBlur,
    onFocus,
    'data-qa-id': dataQaId,
    'aria-label': ariaLabel,
  },
  prefix,
  suffix,
  status = 'neutral',
  prefixVariant = 'default',
  styleVariant = 'default',
  fullHeight,
}: Props) {
  const inputId = useUniqueId({ prefix: 'input-group-', override: id });

  // eslint-disable-next-line consistent-return
  const statusClass = useMemo(() => {
    switch (status) {
      case 'neutral':
        return styles.containerStatusNeutral;
      case 'success':
        return styles.containerStatusSuccess;
      case 'error':
        return styles.containerStatusError;
    }
  }, [status]);

  // eslint-disable-next-line consistent-return
  const stylesVariantClass = useMemo(() => {
    switch (styleVariant) {
      case 'default':
        return styles.containerStyleVariantDefault;
      case 'contrast':
        return styles.containerStyleVariantContrast;
      case 'light-border':
        return styles.containerStyleVariantLightBorder;
    }
  }, [styleVariant]);

  // eslint-disable-next-line consistent-return
  const prefixVariantClass = useMemo(() => {
    switch (prefixVariant) {
      case 'default':
        return styles.containerPrefixVariantDefault;
      case 'transparent':
        return styles.containerPrefixVariantTransparent;
    }
  }, [prefixVariant]);

  return (
    <div
      className={classNames(
        styles.container,
        statusClass,
        stylesVariantClass,
        prefixVariantClass,
        fullHeight ? styles.containerHeightFull : styles.containerHeightRegular
      )}
    >
      {prefix && (
        <label htmlFor={inputId} className={styles.affix}>
          {prefix}
        </label>
      )}

      <input
        id={inputId}
        name={name}
        className={styles.input}
        ref={ref}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        disabled={disabled}
        readOnly={readOnly}
        data-qa-id={dataQaId}
        aria-label={ariaLabel}
      />

      {suffix && (
        <label htmlFor={inputId} className={styles.affix}>
          {suffix}
        </label>
      )}
    </div>
  );
}
